import { useEffect, useRef } from 'react';
import { useTheme } from 'styled-components';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import {
    DonutChartContainer,
    DonutChartWrapper,
    DonutChartLabel,
    DonutChartLegend,
    DonutChartLegendItem,
    DonutChartLegendTopRow,
    DonutChartLegendLeft,
    DonutChartLegendBar,
    DonutChartLegendLabel,
    DonutChartLegendValue,
    DonutChartLegendPercentage
} from './style';

am4core.useTheme(am4themes_animated);

const DonutChart = ({ data = [], $dark = false }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const theme = useTheme();
    const isDark = theme.colors.background.default === '#141414';

    useEffect(() => {
        if (!chartRef.current) return;

        // Determine gap color based on card background
        // Top Performing always uses #292929 background regardless of theme
        const gapColor = $dark ? '#292929' : (isDark ? '#292929' : '#ffffff');

        // Create chart instance
        const chart = am4core.create(chartRef.current, am4charts.PieChart);

        // Initial animation - fade in and scale up
        chart.hiddenState.properties.opacity = 0;
        chart.hiddenState.properties.scale = 0.8;

        // Set chart size
        chart.width = am4core.percent(100);
        chart.height = am4core.percent(100);

        // Remove wave lines and ensure no extra elements
        chart.padding(0, 0, 0, 0);

        // Ensure chart doesn't clip content
        chart.maskBullets = false;

        // Allow chart to extend beyond bounds
        if (chart.chartContainer) {
            chart.chartContainer.overflow = "visible";
        }

        // Set overflow on SVG after chart is created (like Flutter Stack overflow)
        setTimeout(() => {
            if (chartRef.current) {
                const svg = chartRef.current.querySelector('svg');
                if (svg) {
                    // Remove any clipPath that might be clipping content
                    const clipPaths = svg.querySelectorAll('clipPath, defs clipPath');
                    clipPaths.forEach(clip => clip.remove());

                    // Remove wave lines - find and remove any path elements that look like waves
                    const paths = svg.querySelectorAll('path');
                    paths.forEach(path => {
                        const d = path.getAttribute('d');
                        // Remove paths that look like wave lines (curved lines)
                        if (d && (d.includes('C') || d.includes('Q') || d.includes('S'))) {
                            // Check if it's not part of the pie slices (pie slices have M and A commands)
                            if (!d.includes('A') && !d.includes('M 0')) {
                                path.remove();
                            }
                        }
                    });

                    // Remove any polyline or line elements that might be wave lines
                    const polylines = svg.querySelectorAll('polyline, line');
                    polylines.forEach(el => {
                        // Remove if it's not a stroke for the pie slices
                        if (!el.closest('g[class*="slice"]')) {
                            el.remove();
                        }
                    });

                    // Remove clip-path attributes from all elements
                    const allElements = svg.querySelectorAll('*');
                    allElements.forEach(el => {
                        if (el.hasAttribute('clip-path')) {
                            el.removeAttribute('clip-path');
                        }
                        el.style.clipPath = 'none';
                        el.style.overflow = 'visible';
                    });

                    // Set SVG overflow
                    svg.style.overflow = 'visible';
                    svg.style.position = 'relative';
                    svg.style.zIndex = '10';
                    svg.removeAttribute('clip-path');

                    // Expand viewBox to allow overflow (add 50px padding on all sides)
                    const viewBox = svg.getAttribute('viewBox');
                    if (viewBox) {
                        const [x, y, width, height] = viewBox.split(' ').map(Number);
                        svg.setAttribute('viewBox', `${x - 50} ${y - 50} ${width + 100} ${height + 100}`);
                    }

                    // Also set on all groups inside SVG
                    const groups = svg.querySelectorAll('g');
                    groups.forEach(g => {
                        g.style.overflow = 'visible';
                        g.removeAttribute('clip-path');
                    });

                    // Update white colors to black in dark mode
                    if (series.dataItems) {
                        // Keep original colors - no conversion needed
                    }
                }
            }
        }, 300);

        // Set inner radius for donut (50% makes it a donut)
        chart.innerRadius = am4core.percent(50);

        // Set radius
        chart.radius = am4core.percent(100);

        // Create series
        const series = chart.series.push(new am4charts.PieSeries());
        series.dataFields.value = "value";
        series.dataFields.category = "name";

        // Add animation to slices on initial load - slices expand from outside like hover
        series.slices.template.hiddenState.properties.opacity = 0;
        series.slices.template.hiddenState.properties.scale = 0;
        series.slices.template.hiddenState.properties.pullOutDistance = 0.3;

        // Animation duration and easing for initial load
        series.slices.template.defaultState.transitionDuration = 800;
        series.slices.template.defaultState.transitionEasing = am4core.ease.cubicOut;

        // Animate each slice individually with a slight delay
        series.slices.template.adapter.add("hiddenState", function (hiddenState, target) {
            if (target.dataItem) {
                const index = target.dataItem.index;
                hiddenState.transitionDuration = 800;
                hiddenState.transitionDelay = index * 100; // Stagger animation
            }
            return hiddenState;
        });

        // Add gaps between slices (20px visual gap = 10px stroke width on each side)
        series.slices.template.stroke = am4core.color(gapColor);
        series.slices.template.strokeWidth = 10; // Creates ~20px gap between slices
        series.slices.template.strokeOpacity = 1;

        // Set colors from data - keep original colors
        const adjustedColors = data.map(item => {
            return am4core.color(item.color);
        });
        series.colors.list = adjustedColors;

        // Configure slice appearance with corner radius
        series.slices.template.cornerRadius = 8; // Rounded corners for all slices
        series.slices.template.innerCornerRadius = 8; // Rounded inner corners

        // Remove wave lines (disable labels and ticks)
        series.labels.template.disabled = true;
        series.ticks.template.disabled = true;

        // Also disable any other elements that might show wave lines
        if (series.labels) {
            series.labels.template.disabled = true;
        }
        if (series.ticks) {
            series.ticks.template.disabled = true;
        }

        // Calculate total for percentage
        const total = data.reduce((sum, item) => sum + item.value, 0);

        // Enable tooltips to show data on hover
        series.slices.template.tooltipText = "[bold]{name}[/]\nValue: ${value.formatNumber('#,###')}\nPercentage: {percentage.formatNumber('#.00')}%";
        series.slices.template.showTooltipOn = "hover";

        // Configure tooltip styling using adapter - match slice color
        series.slices.template.adapter.add("tooltip", function (tooltip, target) {
            if (tooltip && target && target.dataItem) {
                // Get the slice color
                const sliceColor = target.dataItem.dataContext.color;
                const sliceColorStr = typeof sliceColor === 'string' ? sliceColor : sliceColor.hex;

                // Determine text color based on background (white for dark colors, black for light)
                const isLightColor = sliceColorStr === '#FFFFFF' || sliceColorStr === '#ffffff' ||
                    sliceColorStr === '#FFFFFF' || sliceColorStr.toLowerCase() === '#ffffff';
                const textColor = isLightColor ? '#000000' : '#ffffff';

                tooltip.getFillFromObject = false;
                tooltip.fill = am4core.color(sliceColorStr);
                tooltip.fillOpacity = 1;
                tooltip.stroke = am4core.color("transparent");
                tooltip.strokeWidth = 0;
                tooltip.strokeOpacity = 0;

                if (tooltip.background) {
                    tooltip.background.cornerRadius = 12;
                    tooltip.background.fill = am4core.color(sliceColorStr);
                    tooltip.background.fillOpacity = 1;
                    tooltip.background.stroke = am4core.color("transparent");
                    tooltip.background.strokeWidth = 0;
                    tooltip.background.strokeOpacity = 0;
                }

                if (tooltip.pointer) {
                    tooltip.pointer.fill = am4core.color(sliceColorStr);
                    tooltip.pointer.fillOpacity = 1;
                    tooltip.pointer.stroke = am4core.color("transparent");
                    tooltip.pointer.strokeWidth = 0;
                }

                if (tooltip.label) {
                    tooltip.label.fill = am4core.color(textColor);
                }
            }
            return tooltip;
        });

        // Configure hover state with smooth animation
        const hoverState = series.slices.template.states.getKey("hover");
        hoverState.properties.pullOutDistance = 0.25; // More pull-out distance
        hoverState.properties.scale = 1.1; // Slightly larger scale

        // Ensure slices can extend beyond chart bounds (like Flutter Stack)
        series.slices.template.zIndex = 100;
        hoverState.properties.zIndex = 1000; // Higher z-index on hover to appear above everything

        // Add smooth animation transitions
        series.slices.template.propertyFields.fill = "color";
        series.slices.template.stroke = am4core.color(gapColor);
        series.slices.template.strokeWidth = 10; // Maintain gap on hover

        // Animation settings for smooth transitions
        series.slices.template.defaultState.transitionDuration = 600; // Smooth animation duration (ms)
        hoverState.transitionDuration = 600; // Smooth hover animation (ms)

        // Use easing for smooth animation
        hoverState.transitionEasing = am4core.ease.cubicOut;
        series.slices.template.defaultState.transitionEasing = am4core.ease.cubicOut;

        // Set data - convert white to black in dark mode
        // In dark mode, the card background is white, so white slices should be black
        chart.data = data.map(item => {
            let adjustedColor = item.color;
            // Keep original color - no conversion needed
            const percentage = (item.value / total) * 100;
            return {
                name: item.name,
                value: item.value,
                percentage: percentage,
                color: adjustedColor
            };
        });

        // Disable legend (we'll use custom legend)
        chart.legend = new am4charts.Legend();
        chart.legend.disabled = true;

        // Configure tooltip after chart is fully initialized - will be overridden by event handler
        setTimeout(() => {
            try {
                // Method 1: Configure via series template
                if (series && series.slices && series.slices.template) {
                    const tooltip = series.slices.template.tooltip;
                    if (tooltip) {
                        tooltip.getFillFromObject = false;
                        tooltip.fillOpacity = 1;
                        tooltip.stroke = am4core.color("transparent");
                        tooltip.strokeWidth = 0;
                        tooltip.strokeOpacity = 0;

                        if (tooltip.background) {
                            tooltip.background.cornerRadius = 12;
                            tooltip.background.fillOpacity = 1;
                            tooltip.background.stroke = am4core.color("transparent");
                            tooltip.background.strokeWidth = 0;
                            tooltip.background.strokeOpacity = 0;
                        }

                        if (tooltip.pointer) {
                            tooltip.pointer.fillOpacity = 1;
                            tooltip.pointer.stroke = am4core.color("transparent");
                            tooltip.pointer.strokeWidth = 0;
                        }
                    }
                }
            } catch (e) {
                console.error("Tooltip configuration error:", e);
            }
        }, 500);

        // Also configure on every tooltip show event - match slice color
        series.slices.template.events.on("over", function (ev) {
            setTimeout(() => {
                try {
                    const tooltip = ev.target.tooltip;
                    const dataItem = ev.target.dataItem;
                    if (tooltip && dataItem) {
                        // Get the slice color
                        const sliceColor = dataItem.dataContext.color;
                        const sliceColorStr = typeof sliceColor === 'string' ? sliceColor : sliceColor.hex;

                        // Determine text color based on background
                        const isLightColor = sliceColorStr === '#FFFFFF' || sliceColorStr === '#ffffff' ||
                            sliceColorStr.toLowerCase() === '#ffffff';
                        const textColor = isLightColor ? '#000000' : '#ffffff';

                        tooltip.getFillFromObject = false;
                        tooltip.fill = am4core.color(sliceColorStr);
                        tooltip.fillOpacity = 1;
                        tooltip.stroke = am4core.color("transparent");
                        tooltip.strokeWidth = 0;

                        if (tooltip.background) {
                            tooltip.background.cornerRadius = 12;
                            tooltip.background.fill = am4core.color(sliceColorStr);
                            tooltip.background.fillOpacity = 1;
                            tooltip.background.stroke = am4core.color("transparent");
                            tooltip.background.strokeWidth = 0;
                        }

                        if (tooltip.pointer) {
                            tooltip.pointer.fill = am4core.color(sliceColorStr);
                            tooltip.pointer.fillOpacity = 1;
                            tooltip.pointer.stroke = am4core.color("transparent");
                            tooltip.pointer.strokeWidth = 0;
                        }

                        if (tooltip.label) {
                            tooltip.label.fill = am4core.color(textColor);
                        }
                    }
                } catch (e) {
                    console.error("Tooltip event configuration error:", e);
                }
            }, 10);
        });

        // Store instance
        chartInstance.current = chart;

        // Cleanup
        return () => {
            if (chartInstance.current) {
                chartInstance.current.dispose();
            }
        };
    }, [data, isDark, $dark]);

    // Calculate total for center label
    const total = data.reduce((sum, item) => sum + item.value, 0);
    const totalFormatted = total >= 1000 ? `$${total / 1000}k` : `$${total}`;

    return (
        <DonutChartContainer $dark>
            <DonutChartWrapper>
                <div
                    ref={chartRef}
                    style={{
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        zIndex: 10,
                        overflow: 'visible',
                        margin: '0 auto',
                        pointerEvents: 'auto'
                    }}
                />
                <DonutChartLabel $dark={$dark}>
                    <div>Total</div>
                    <div>{totalFormatted}</div>
                </DonutChartLabel>
            </DonutChartWrapper>
            <DonutChartLegend>
                {data.slice(0, 3).map((item, index) => {
                    // Keep original color
                    const adjustedColor = item.color;

                    return (
                        <DonutChartLegendItem key={index}>
                            <DonutChartLegendTopRow>
                                <DonutChartLegendLeft>
                                    <DonutChartLegendBar $color={adjustedColor} $dark={$dark} />
                                    <DonutChartLegendLabel $isEnterprise={item.name === 'Enterprise'} $dark={$dark}>
                                        {item.name}
                                    </DonutChartLegendLabel>
                                </DonutChartLegendLeft>
                                <DonutChartLegendPercentage>
                                    {item.percentage}%
                                </DonutChartLegendPercentage>
                            </DonutChartLegendTopRow>
                            <DonutChartLegendValue $dark={$dark}>
                                ${item.value.toLocaleString()}
                            </DonutChartLegendValue>
                        </DonutChartLegendItem>
                    );
                })}
            </DonutChartLegend>
        </DonutChartContainer>
    );
};

export default DonutChart;

